import { useMemo, useState } from "react";

import FlowBars from "../../components/director/module1/FlowBars";
import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { useDirectorContext } from "@contexts/director";
import ScenariosPanel from "../../components/director/scenarios/ScenariosPanel";
import { formatCents } from "../../utils/directorFormat";
import { currentCompetence, moduleFilters, scenarioLabel } from "./module1Utils";

const originDate = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit" });

// De onde vêm os parâmetros vigentes: o BE diz a origem; aqui só se escreve em português.
const originNote = (source, updatedAt) => {
  if (source === "portfolio") return "do portfólio";
  if (source === "director") {
    const time = typeof updatedAt === "string" ? Date.parse(updatedAt) : NaN;
    return Number.isNaN(time) ? "do diretor" : `do diretor, em ${originDate.format(new Date(time))}`;
  }
  return null;
};

// pt-BR com até 2 casas (o BE aceita bps inteiros): 1050 -> "10,5%", 1234 -> "12,34%".
const percentFormatter = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const percentText = (bps) => `${percentFormatter.format((Number.isFinite(bps) ? bps : 0) / 100)}%`;

const Scenarios = () => {
  const { filters, selectedCompanyId } = useDirectorContext();
  const [scenario, setScenario] = useState("base");
  // Só esta tela pede as premissas do diretor; as outras leem o materializado (SPEC §3 item 9).
  const query = useMemo(() => ({
    ...moduleFilters({ from: currentCompetence(), months: 24, scenario, filters }),
    assumptions: "director",
  }), [filters, scenario]);
  const weekly = useDirectorSurface("cashflow/weekly", query);
  const fallback = weekly.data?.assumptionsSource === "materialized" && Boolean(weekly.data?.assumptionsFallbackReason);
  // Depois de salvar, mostra um cenário que mudou: o atual se mudou, senão Conservador, senão Estresse.
  const showSaved = (changed = []) => {
    const target = changed.includes(scenario)
      ? scenario
      : ["conservative", "stress"].find((key) => changed.includes(key));
    if (target && target !== scenario) setScenario(target);
    else weekly.reload();
    return target ?? null;
  };
  const series = weekly.data?.series || [];
  const total = series.reduce((value, item) => value + item.netFlowCents, 0);
  const assumptions = weekly.data?.assumptions || {};
  const origin = originNote(weekly.data?.assumptionsSource, weekly.data?.assumptionsUpdatedAt);
  const withOrigin = (note) => (origin ? `${note} · ${origin}` : note);
  return <ModuleShell
    title="Cenários de fluxo"
    description="Base, conservador e estresse aplicados aos mesmos fatos, com premissas visíveis."
    badge={scenarioLabel(scenario)}
    qualitySources={[weekly]}
  >
    <section className="director-module1__filters"><label className="director-filter-field"><span>Cenário</span><select value={scenario} onChange={(event) => setScenario(event.target.value)}><option value="base">Base</option><option value="conservative">Conservador</option><option value="stress">Estresse</option></select></label></section>
    <ScenariosPanel companyId={selectedCompanyId} onSaved={showSaved} />
    <ModuleState {...weekly} availability={weekly.data?.availability} onRetry={weekly.reload} />
    {weekly.data && <>
      {fallback && <p className="director-scenarios__fallback" role="note">
        Os parâmetros do diretor não puderam ser aplicados nesta leitura; mostrando o cenário do portfólio.
      </p>}
      <section className="director-module1__metrics director-module1__metrics--four">
        <MetricCard label="Fluxo líquido · 13 semanas" value={formatCents(total)} note="Entradas menos saídas" tone={total < 0 ? "pressure" : "green"} />
        <MetricCard label="Atraso de entradas" value={`${assumptions.inflowDelayDays || 0} dias`} note={withOrigin("Somente parcelas futuras")} tone="blue" />
        <MetricCard label="Corte de entradas" value={percentText(assumptions.futureInflowHaircutBps)} note={withOrigin("Sobre entradas futuras")} tone="orange" />
        <MetricCard label="Acréscimo da saída não comprometida" value={percentText(assumptions.uncommittedOutflowUpliftBps)} note={withOrigin("Sobre saídas ainda não comprometidas")} tone="pressure" />
      </section>
      <section className="director-module1__split"><article className="director-module1__panel director-module1__panel--bars"><header><div><span className="director-eyebrow">Efeito do cenário</span><h2>13 semanas</h2></div></header><FlowBars items={series} /></article><aside className="director-module1__insight"><span className="director-eyebrow">Limite do módulo</span><h2>Simular não executa</h2><p>Esta tela altera somente a leitura interna. Empréstimo, aporte e renegociação pertencem ao Módulo 5 e permanecem fora deste fluxo.</p></aside></section>
    </>}
  </ModuleShell>;
};

export default Scenarios;
